'use client';

import { c as _c } from "react/compiler-runtime";
import { canRetryFundingAuthentication } from '@morfi/onramp-sdk';
import type { AuthEvent, ConnectEvent, MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
interface Props {
  provider: Pick<MoonPayWebProvider, 'connect' | 'setupAuth'>;
  mismatch: boolean;
  onComplete(): void;
}
export function FundingRecoveryAuth(props) {
  const $ = _c(4);
  const [revision, setRevision] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setRevision(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props || $[2] !== revision) {
    t1 = <RecoveryAuthFrame key={revision} {...props} onRetry={t0} />;
    $[1] = props;
    $[2] = revision;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp(value) {
  return value + 1;
}
function RecoveryAuthFrame({
  provider,
  mismatch,
  onComplete,
  onRetry
}: Props & {
  onRetry(): void;
}): ReactNode {
  const t = useTranslations('onramp');
  const element = useRef<HTMLDivElement>(null);
  const completed = useEffectEvent(onComplete);
  const [failed, setFailed] = useState(false);
  const [rejected, setRejected] = useState(false);
  const authenticationRejected = useEffectEvent((): void => {
    setRejected(true);
    setFailed(true);
    showToast({
      id: TOAST_IDS.ONRAMP_AUTH_REJECTED,
      message: t('verification_failed'),
      variant: 'error'
    });
    logger.warn('onramp.authentication_rejected', {
      phase: 'recovery'
    });
  });
  useEffect(() => {
    const container = element.current;
    if (!container) return;
    const owned = document.createElement('div');
    container.appendChild(owned);
    let active = true;
    let authRejected = false;
    let dispose: (() => void) | undefined;
    async function open(): Promise<void> {
      await Promise.resolve();
      if (!active) return;
      try {
        const result = await (mismatch ? provider.connect : provider.setupAuth)({
          container: owned,
          onEvent: (event: AuthEvent | ConnectEvent): void => {
            if (!active) return;
            if (event.kind === 'complete') completed();else if (event.kind === 'error') {
              if (canRetryFundingAuthentication(event.payload)) {
                authRejected = true;
                authenticationRejected();
              } else fail();
            }
          }
        });
        if (result.ok) {
          if (active) dispose = result.value.dispose;else result.value.dispose();
        } else if (!authRejected) fail();
      } catch {
        if (active) {
          logger.warn('onramp.action_recovery_auth_failed');
          setFailed(true);
        }
      }
    }
    function fail(): void {
      if (!active) return;
      logger.warn('onramp.action_recovery_auth_failed');
      setFailed(true);
    }
    void open();
    return () => {
      active = false;
      dispose?.();
      owned.remove();
    };
  }, [provider, mismatch]);
  return <div className="flex flex-col gap-3">
      <div ref={element} hidden={failed} className="min-h-96 w-full" />
      {failed ? <>
          <p role="alert">{t(rejected ? 'verification_failed' : 'provider_unavailable')}</p>
          <AnimatedButton onClick={(): void => {
        setFailed(false);
        onRetry();
      }}>
            {t('retry')}
          </AnimatedButton>
        </> : null}
    </div>;
}